import React, { useState, useEffect } from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';
import { connect } from 'react-redux';

import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';
import { scale as initialScale, is24HourFormat } from '@components/frame';
import {
  formatSinceTime,
  isPhone,
  subStatusName,
  machineStatusName,
  subStatuses,
  convertTime,
  navigateTo,
  formatSinceTimeWithDayTranslationCase,
} from '../../../../helpers';
import { font as initialFont } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import { switchConnection, getMachines } from '@reducers/machines';
import { useForceUpdate } from '@hooks/useForceUpdate';
import { useForceUpdateEverySecond } from '@v2/pages/common/hooks/useForceUpdateEverySecond';

const NcFileTime = React.memo(
  ({ C, s, font, subStatus, timeOffset, lastUpdatedOn }) => {
    useForceUpdateEverySecond();

    const time = moment.utc().add(timeOffset, 'milliseconds');

    const lastModifiedMilliseconds = moment.utc(lastUpdatedOn).valueOf();

    const lastTime = Math.round((time.utc().valueOf() - lastModifiedMilliseconds) / 1000);

    const isMoreHour = moment.utc().diff(moment.utc(lastUpdatedOn), 'm') >= 60;
    const isRunning = subStatus === subStatuses.Running;

    return (
      <Text
        style={[
          {
            color: C('Label/Dark/Secondary'),
            fontVariant: ['tabular-nums'],
          },
          font['Subhead 1'],
        ]}
      >
        {isRunning || !isMoreHour ? `${convertTime(lastTime)}` : `${formatSinceTimeWithDayTranslationCase(lastUpdatedOn, is24HourFormat)}`}
      </Text>
    );
  },
  (prev, next) =>
    prev.timeOffset === next.timeOffset &&
    prev.subStatus === next.subStatus &&
    prev.lastUpdatedOn === next.lastUpdatedOn &&
    prev.C.scheme === next.C.scheme
);

const ConnectingMachine = ({ selectedItem, C, timeOffset, font = initialFont, scale = initialScale, switchConnection, getMachines }) => {
  const { machineStatus, id } = selectedItem || {};
  const { subStatus, lastUpdatedOn, status, executionMode } = machineStatus || {};

  const [connectionLoading, setConnectionLoading] = useState(false);

  const s = scale;

  const useSubstatusArray = [
    subStatuses.Offline,
    subStatuses.ConnectionFailed,
    subStatuses.NotPingable,
    subStatuses.ConnectionOff,
    subStatuses.MachineConnectorOff,
    subStatuses.ConnectorOff,
    subStatuses.NoConnector,
  ];

  const useSubstatus = useSubstatusArray.includes(subStatus);

  const textValue = useSubstatus ? subStatus : status;
  const textStorage = useSubstatus ? subStatusName : machineStatusName;

  const bottomTextStorage = {
    [subStatuses.Offline]: 'The machine is pingable but a connection is not possible e g because access was denied on the machine',
    [subStatuses.ConnectionFailed]: 'The machine is pingable but a connection is not possible e g because access was denied on the machine',
    [subStatuses.NotPingable]: 'The machine is not pingable on the network',
  };

  const showIconArray = [subStatuses.MachineConnectorOff, subStatuses.ConnectionOff, subStatuses.ConnectorOff, subStatuses.NoConnector];

  const showIcon = showIconArray.includes(subStatus);

  const iconStorage = {
    [subStatuses.MachineConnectorOff]: 'machineConnectionOffLarge',
    [subStatuses.ConnectionOff]: 'machineConnectionOff',
    [subStatuses.ConnectorOff]: 'connectorOfflineLarge',
    [subStatuses.NoConnector]: 'noConnectorLarge',
  };

  const triggerConnection = async (value) => {
    setConnectionLoading(true);
    await switchConnection({
      machineId: id,
      switchedOn: true,
    });
    await getMachines();
    setConnectionLoading(false);
  };

  const buttonTypes = {
    switchOnMachine: {
      text: 'Switch Machine Connection On',
      action: () => triggerConnection(),
    },
    goToConnectorSetting: {
      text: 'Go To Connector Settings',
      action: () => {
        navigateTo('Settings', { screen: 'Connector' });
      },
    },
  };

  const buttonStorage = {
    [subStatuses.MachineConnectorOff]: 'switchOnMachine',
    [subStatuses.ConnectionOff]: 'switchOnMachine',
    [subStatuses.ConnectorOff]: 'goToConnectorSetting',
    [subStatuses.NoConnector]: 'goToConnectorSetting',
  };

  const button = buttonTypes[buttonStorage[subStatus]];

  return (
    <View>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', alignContent: 'center' }}>
        <View>
          <Text style={[{ color: C('Label/Light/Primary'), textAlign: 'center' }, font.Body]}>{i18n.t(textStorage[textValue] || textValue)}</Text>
        </View>
      </View>
      <View style={{ marginTop: s(isPhone ? 1 : 4) }}>
        <View
          style={{
            alignItems: 'center',
            alignContent: 'center',
            justifyContent: 'center',
          }}
        >
          <NcFileTime
            {...{
              subStatus,
              lastUpdatedOn,
              timeOffset,
              font,
              C,
              s,
            }}
          />
          <View style={{ marginTop: s(24), maxWidth: s(324) }}>
            {showIcon ? (
              <View style={{ alignItems: 'center' }}>
                <View style={{ marginBottom: scale(34) }}>
                  <Svg icon={iconStorage[subStatus]} width={s(120)} height={s(80)} color="Label/Light/Error" />
                </View>
                <TouchableElement
                  onPress={() => {
                    button.action();
                  }}
                >
                  {({ boxStyle }) => (
                    <View
                      style={[
                        boxStyle,
                        { backgroundColor: C('Button/Light/Secondary'), borderRadius: s(14), paddingHorizontal: s(13), paddingVertical: s(7) },
                      ]}
                    >
                      {connectionLoading ? (
                        <SmallLoading />
                      ) : (
                        <Text style={[font['Button 2'], { color: C('Button/Light/Secondary Text') }]}>{i18n.t(button.text)}</Text>
                      )}
                    </View>
                  )}
                </TouchableElement>
              </View>
            ) : (
              <Text
                style={[
                  {
                    color: C('Label/Light/Tertiary'),
                    textAlign: 'center',
                  },
                  font['Subhead 1'],
                ]}
              >
                {bottomTextStorage[subStatus] ? i18n.t(bottomTextStorage[subStatus]) : `${i18n.t('Execution Mode')}: ${executionMode ?? ''}`}
              </Text>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

export default connect(null, { switchConnection, getMachines })(ConnectingMachine);
